import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import * as _ from 'lodash';
import {IOrderHistoryView, IOrderPaymentResult, IOrderRepaymentQuote, IOrderViewPayment, OrderPaymentStep, OrderRepaymentStatus} from '../orders-models';
import {IPaymentMethod, PaymentMethodInput, PaymentMethodOpenTemplate, PaymentMethodsResponse} from '../../../checkout/models/payment/payment-method';
import {GqlOrderPaymentService, IReplaceOrderPaymentInput} from '../../../checkout/graphql/order-history/gql-order-payment.service';
import {LoggerService, UsanaLogger} from '../../common/logger.service';
import {PaymentCacheService} from '../../../checkout/payment/payment-cache.service';
import {CheckoutUtil} from '../../../checkout/checkout-util';
import {Constants} from '../../constants';
import {PaymentUtil} from '../../autoorder/payment/payment-util';
import {Util} from '../../util/util';

export enum OrderPaymentModalState {
    LOADING = 'LOADING',
    VIEW = 'VIEW',
    SUBMITTING = 'SUBMITTING',
    // A payment on the order cannot be released, so only Customer Service can resolve it.
    UNCANCELABLE = 'UNCANCELABLE',
    // The order is already covered -- nothing to pay, and nothing wrong.
    ALREADY_PAID = 'ALREADY_PAID'
}

@Component({
    selector: 'order-payment-modal',
    templateUrl: './order-payment-modal.component.html',
    styleUrls: ['../orders.scss', './order-payment-modal.component.scss'],
    standalone: false
})
export class OrderPaymentModalComponent implements OnInit {
    @Input() orderView: IOrderHistoryView;
    @Output() readonly onClose = new EventEmitter();
    @Output() readonly onPaymentComplete = new EventEmitter<number>();

    readonly states = OrderPaymentModalState;
    state: OrderPaymentModalState = OrderPaymentModalState.LOADING;

    readonly liteTemplate = PaymentMethodOpenTemplate.LITE;

    paymentMethods: IPaymentMethod[] = [];
    selectedMethodId: number;
    errorMessage: string;
    addingNewCard: boolean = false;
    cardBusy: boolean = false;
    // Set when the chosen card still needs its CVV associated, which opens the lite card form for it.
    cvvMethodId: number;
    // payment-service decides what the order owes and which payments get released. The client never recomputes it.
    quote: IOrderRepaymentQuote;

    private readonly log: UsanaLogger;

    constructor(private gqlOrderPaymentService: GqlOrderPaymentService,
                private paymentCacheService: PaymentCacheService,
                loggerService: LoggerService) {
        this.log = loggerService.getLogger('OrderPaymentModalComponent');
    }

    ngOnInit() {
        this.state = OrderPaymentModalState.LOADING;
        this.gqlOrderPaymentService.getOrderRepaymentQuote(this.orderView.id).subscribe({
            next: (quote: IOrderRepaymentQuote) => {
                this.quote = quote;
                if (quote?.status === OrderRepaymentStatus.ALREADY_PAID) {
                    // Nothing owed. Not a problem, so it must not read as one.
                    this.state = OrderPaymentModalState.ALREADY_PAID;
                    return;
                }
                if (quote?.status !== OrderRepaymentStatus.READY_TO_CHARGE) {
                    this.state = OrderPaymentModalState.UNCANCELABLE;
                    return;
                }
                this.loadPaymentMethods();
            },
            error: (error: any) => {
                this.log.error('OrderPaymentModalComponent.getOrderRepaymentQuote', error);
                this.errorMessage = 'cart.order.history.payment.error.load';
                this.state = OrderPaymentModalState.VIEW;
            }
        });
    }

    loadPaymentMethods() {
        this.state = OrderPaymentModalState.LOADING;
        this.gqlOrderPaymentService.getOrderPaymentMethods(this.orderView.id).subscribe({
            next: (response: PaymentMethodsResponse) => {
                this.paymentMethods = response?.paymentMethods ?? [];
                this.refreshDisplaySummaries();
                this.state = OrderPaymentModalState.VIEW;
            },
            error: (error: any) => {
                this.log.error('OrderPaymentModalComponent.loadPaymentMethods', error);
                this.errorMessage = 'cart.order.history.payment.error.load';
                this.state = OrderPaymentModalState.VIEW;
            }
        });
    }

    get payments(): IOrderViewPayment[] {
        return [...(this.orderView?.nonVouchersPayments ?? []), ...(this.orderView?.vouchersPayments ?? [])];
    }

    // The payments the quote says are still live, so the list shown matches exactly what gets released.
    get paymentsToReplace(): IOrderViewPayment[] {
        const liveIds = this.quote?.livePaymentIds ?? [];
        return _.filter(this.payments, (payment: IOrderViewPayment) => liveIds.includes(payment.paymentId));
    }

    get hasSettledAmount(): boolean {
        return (this.quote?.settledAmount ?? 0) > 0;
    }

    get selectableMethods(): IPaymentMethod[] {
        return _.filter(this.paymentMethods, (method: IPaymentMethod) => method.onFile);
    }

    get cnofMethod(): IPaymentMethod {
        return _.find(this.paymentMethods, (method: IPaymentMethod) =>
            method.type === Constants.PMT_CARD_NOT_ON_FILE && !method.onFile);
    }

    get selectedMethod(): IPaymentMethod {
        return _.find(this.paymentMethods, (method: IPaymentMethod) => method.id === this.selectedMethodId);
    }

    get isSubmitting(): boolean {
        return this.state === OrderPaymentModalState.SUBMITTING;
    }

    // The amount actually being charged. E-wallet validity is checked against it, not the order total.
    private get chargeContext(): any {
        return {total: this.quote?.remainingBalance};
    }

    // requireCVV2COF countries (CO, MX today) need the CVV re-associated with the stored card token before the
    // card can be charged. openMethodForInvalidPaymentSelected owns that rule but also fires for PayPal and
    // e-wallets, so it is scoped to cards on file -- the lite form reads paymentSource.card and nothing else.
    needsCvv(method: IPaymentMethod): boolean {
        return method?.type === Constants.PMT_CARD_ON_FILE
            && PaymentUtil.openMethodForInvalidPaymentSelected(method, this.chargeContext, false);
    }

    get canSubmit(): boolean {
        return !!this.selectedMethod && !this.isSubmitting
            && PaymentUtil.isValidForPayment(this.selectedMethod, this.chargeContext);
    }

    get canClose(): boolean {
        return !this.isSubmitting && !this.cardBusy;
    }

    private refreshDisplaySummaries() {
        _.forEach(this.paymentMethods, (method: IPaymentMethod) => {
            method.displaySummary = PaymentUtil.getSummary(method, method.id === this.selectedMethodId, false);
        });
    }

    selectMethod(method: IPaymentMethod) {
        this.errorMessage = undefined;
        this.addingNewCard = false;
        this.selectedMethodId = method.id;
        this.cvvMethodId = this.needsCvv(method) ? method.id : undefined;
        this.refreshDisplaySummaries();
    }

    trackByFn(index: any) {
        return Util.trackByFn(index);
    }

    startAddCard() {
        this.errorMessage = undefined;
        this.selectedMethodId = undefined;
        this.cvvMethodId = undefined;
        this.addingNewCard = true;
        this.refreshDisplaySummaries();
    }

    onCardBusy(busy: boolean) {
        this.cardBusy = busy;
    }

    onCardCancel() {
        this.cardBusy = false;
        this.addingNewCard = false;
        this.cvvMethodId = undefined;
        this.selectedMethodId = undefined;
        this.refreshDisplaySummaries();
    }

    onCardEntered() {
        this.cardBusy = false;
        // The lite form only associates the CVV with the stored token, so the saved card submits as normal.
        if (this.cvvMethodId) {
            this.cvvMethodId = undefined;
            this.submit();
            return;
        }
        const selected = this.paymentCacheService.getSelectedPayment();
        const paymentMethod = CheckoutUtil.mergeKeepShape(new PaymentMethodInput(), selected);
        this.submitWithPaymentMethod(paymentMethod);
    }

    submit() {
        if (!this.canSubmit) {
            return;
        }
        const method = this.selectedMethod;
        const paymentMethod = {
            type: method.type,
            id: method.id,
            onFile: method.onFile
        } as PaymentMethodInput;
        this.submitWithPaymentMethod(paymentMethod);
    }

    private submitWithPaymentMethod(paymentMethod: PaymentMethodInput) {
        this.errorMessage = undefined;
        this.state = OrderPaymentModalState.SUBMITTING;

        const input: IReplaceOrderPaymentInput = {orderId: this.orderView.id, paymentMethod};

        this.gqlOrderPaymentService.replaceOrderPayment(input).subscribe({
            next: (result: IOrderPaymentResult) => this.handleResult(result),
            error: (error: any) => {
                this.log.error('OrderPaymentModalComponent.submit', error);
                this.errorMessage = 'cart.order.history.payment.error.submit';
                this.state = OrderPaymentModalState.VIEW;
                this.addingNewCard = false;
            }
        });
    }

    private handleResult(result: IOrderPaymentResult) {
        if (result?.success) {
            this.onPaymentComplete.emit(this.orderView.id);
            return;
        }

        this.errorMessage = result?.failedStep === OrderPaymentStep.CANCEL
            ? 'cart.order.history.payment.error.cancel'
            : 'cart.order.history.payment.error.submit';
        this.state = OrderPaymentModalState.VIEW;
        this.addingNewCard = false;
    }

    close() {
        if (this.canClose) {
            this.onClose.emit();
        }
    }
}
